<template>
  <div v-if="cfg" class="funnel-bundel bg-white">

    <!-- ── Hero ─────────────────────────────────────────── -->
    <section class="bg-[#111] text-white">
      <div class="max-w-screen-xl mx-auto px-6 py-14 lg:py-20 text-center">
        <p class="text-[0.875rem] font-semibold text-[#FFD500] mb-4 uppercase tracking-[0.03em]">
          <span aria-hidden="true">📦</span> Complete bundel
        </p>
        <h1 class="text-white text-[clamp(2rem,5vw,3.25rem)] font-black leading-[1.1] mb-4 max-w-[820px] mx-auto">
          {{ cfg.title }}
        </h1>
        <p class="text-[1.0625rem] sm:text-[1.1875rem] leading-[1.7] text-[#ccc] max-w-[620px] mx-auto">
          {{ cfg.sub }}
        </p>

        <!-- Voordeel-strip -->
        <div class="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mt-8 text-[0.9rem] text-[#bbb]">
          <span class="flex items-center gap-2"><i class="bi bi-box-seam text-[#FFD500]"></i> Alles in één bestelling</span>
          <span class="flex items-center gap-2"><i class="bi bi-piggy-bank text-[#FFD500]"></i> Voordeliger dan los</span>
          <span class="flex items-center gap-2"><i class="bi bi-truck text-[#FFD500]"></i> Samen bezorgd</span>
        </div>
      </div>
    </section>

    <!-- ── Bundel-uitleg ────────────────────────────────── -->
    <section class="max-w-screen-xl mx-auto px-6 py-12 lg:py-16">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
        <div v-for="(part, i) in cfg.parts" :key="i" class="flex items-start gap-4 bg-[#fafafa] rounded-[14px] p-5 border border-[#eee]">
          <span class="w-10 h-10 rounded-[8px] bg-[#fffbe6] text-[#FFD500] flex items-center justify-center shrink-0">
            <i :class="['bi', part.icon]"></i>
          </span>
          <div>
            <strong class="block text-[1rem] text-[#111] mb-1">{{ part.title }}</strong>
            <p class="text-[0.875rem] text-[#666] leading-[1.5] m-0">{{ part.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Producten ────────────────────────────────────── -->
    <section class="max-w-screen-xl mx-auto px-6 pb-16 lg:pb-20">
      <h2 class="text-[clamp(1.5rem,3vw,2.25rem)] font-black text-[#111] mb-2">Stel je bundel samen</h2>
      <p class="text-[1rem] text-[#666] mb-8">Kies je onderdelen - voeg toe aan je winkelwagen en profiteer van gratis verzending vanaf €50.</p>

      <!-- Loading -->
      <div v-if="loading" class="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <div v-for="n in 4" :key="n" class="rounded-[14px] bg-[#f3f3f3] animate-pulse aspect-[3/4]"></div>
      </div>

      <!-- Grid -->
      <div v-else-if="products.length" class="products-grid grid grid-cols-2 lg:grid-cols-4 gap-5">
        <ProductCard v-for="(p, i) in products" :key="p.id" :product="p" :index="i" />
      </div>

      <!-- Lege staat -->
      <div v-else class="text-center py-12 bg-[#fafafa] rounded-[14px] border border-[#eee]">
        <p class="text-[#666] mb-4">Er zijn op dit moment geen producten in deze bundel beschikbaar.</p>
        <router-link :to="cfg.categoryLink" class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-6 py-3 rounded-[8px] no-underline transition-colors">
          Bekijk de categorie <i class="bi bi-arrow-right"></i>
        </router-link>
      </div>
    </section>

    <!-- ── Slot-CTA ─────────────────────────────────────── -->
    <section class="bg-[#FFD500] py-14">
      <div class="max-w-screen-xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
        <div>
          <h2 class="text-[clamp(1.5rem,3vw,2rem)] font-black text-[#111] mb-1">Hulp bij samenstellen?</h2>
          <p class="text-[#333] text-[1rem] m-0">Onze specialisten stellen gratis de ideale bundel voor je samen.</p>
        </div>
        <router-link to="/gratis-energieadvies" class="shrink-0 bg-[#111] text-white font-bold text-[1.0625rem] px-8 py-4 rounded-[8px] no-underline inline-flex items-center gap-2 hover:bg-[#333] funnel-cta">
          Gratis advies <i class="bi bi-arrow-right"></i>
        </router-link>
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { useProductStore } from '@stores/products'
import ProductCard from '@components/ProductCard.vue'

const route = useRoute()
const productStore = useProductStore()

const BUNDELS = {
  'balkon-zonnepanelen': {
    title: 'Complete balkon zonnepanelen bundel',
    sub: 'Panelen, micro-omvormer en bevestiging - alles wat je nodig hebt om direct te beginnen met besparen.',
    categorySlug: 'balkon-zonnepanelen',
    categoryLink: '/categorie/balkon-zonnepanelen',
    parts: [
      { icon: 'bi-sun', title: 'Zonnepanelen', text: 'All-black panelen, afgestemd op de ruimte van je balkon.' },
      { icon: 'bi-gear', title: 'Micro-omvormer', text: 'Zet de opgewekte stroom om naar bruikbare netstroom.' },
      { icon: 'bi-wrench', title: 'Bevestiging & stekker', text: 'Beugels en de vereiste Schuko-stekker inbegrepen.' },
    ],
  },
  'thuisbatterij': {
    title: 'Thuisbatterij + accessoires bundel',
    sub: 'Combineer je thuisbatterij met de juiste kabels en accessoires voor een compleet, werkend systeem.',
    categorySlug: 'thuisbatterijen',
    categoryLink: '/categorie/thuisbatterijen',
    parts: [
      { icon: 'bi-battery-full', title: 'Thuisbatterij', text: 'Kies de capaciteit die past bij je verbruik en opwek.' },
      { icon: 'bi-boxes', title: 'Uitbreidingen', text: 'Modulair uit te breiden wanneer je verbruik groeit.' },
      { icon: 'bi-tools', title: 'Accessoires', text: 'Kabels, BMS en meer om je systeem compleet te maken.' },
    ],
  },
}

const cfg = computed(() => BUNDELS[route.params.slug] || null)

// Dynamische SEO per bundel-slug
watchEffect(() => {
  if (typeof document === 'undefined') return
  const c = cfg.value
  if (!c) return
  document.title = `${c.title} | Plug2Charge.nl`
  const desc = document.querySelector('meta[name="description"]')
  if (desc) desc.setAttribute('content', c.sub || '')
})

const loading = ref(true)
const products = ref([])

async function load() {
  if (!cfg.value) { loading.value = false; return }
  loading.value = true
  try {
    const all = await productStore.fetchAllProducts()
    const slug = cfg.value.categorySlug
    products.value = (all || [])
      .filter(p => Array.isArray(p.categories) && p.categories.some(c => c.slug === slug))
      .slice(0, 8)
  } catch (e) {
    products.value = []
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(() => route.params.slug, load)
</script>

<style scoped>
.funnel-cta {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.funnel-cta:hover  { transform: translateY(-2px); }
.funnel-cta:active { transform: scale(0.98); }
@media (prefers-reduced-motion: reduce) { .funnel-cta { transition: none; } }
</style>
